import { moneyFormat } from '@4beefopag/4bee-fopag-core/dist/util';
import { Column } from '@ant-design/charts';
import { Radio, Skeleton } from 'antd';
import moment from 'moment';
import 'moment/locale/pt-br';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import styled from 'styled-components';
import usePersistedState from '../../../../hooks/usePersistedState';
import usePayrollValueBandsChart from '../usePayrollValueBandsChart';
import { sumBandsTotalValue } from '../payrollValueBands';
import { pluralize } from '../../../../services/util/string';
import LoadError from '../LoadError';
import TotalChart, { TOTAL_CHART_TYPES, TOTAL_METRICS } from './TotalChart';

const ChartCard = styled.div`
  margin-top: 24px;
  padding: 24px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const ChartHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
`;

const TotalChartOptions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 24px;
  margin-bottom: 16px;
`;

const OptionLabel = styled.span`
  margin-right: 8px;
  color: rgba(0, 0, 0, 0.65);
`;

const ChartTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
`;

const ChartWrapper = styled.div`
  height: 360px;
`;

// Cores do design do Figma, com os tons de âmbar (5000-10000) e marrom (30000+) ajustados
// para passar em validação de contraste e distinção sob daltonismo. A ordem das chaves aqui
// precisa acompanhar a ordem de PAYROLL_VALUE_BANDS no backend: o G2Plot mapeia esta lista de
// cores posicionalmente contra o domínio fixado em `meta.bandLabel.values` (ver `config` abaixo) —
// ele não repassa o datum inteiro para uma função de cor quando `seriesField` está em uso, só o
// próprio valor de `bandLabel`, então colorir por callback com base em `faixaId` não funciona.
export const COLOR_BY_BAND_ID = {
  '0-5000': '#13C2C2',
  '5000-10000': '#FAAD14',
  '10000-15000': '#FF4D4F',
  '15000-20000': '#4ea3ff',
  '20000-25000': '#52C41A',
  '25000-30000': '#FF7875',
  '30000+': '#D48806',
};

export const CHART_VIEWS = { BY_BAND: 'byBand', TOTAL: 'total' };

const BY_BAND_TITLE = 'Quantidade Cooperados por Faixa de Remuneração';

const TOTAL_TITLE_BY_METRIC = {
  [TOTAL_METRICS.VALUE]: 'Valor Total da Folha',
  [TOTAL_METRICS.COOPERATORS]: 'Quantidade Total de Cooperados',
};

const PREFERENCES_STORAGE_KEY = 'fopag.dashboard.value-bands-chart';

// Pedido do PO: na visão Total o valor da folha fica em destaque, em barras horizontais que comportam
// períodos longos com o rótulo de cada competência legível
export const DEFAULT_PREFERENCES = {
  view: CHART_VIEWS.BY_BAND,
  totalMetric: TOTAL_METRICS.VALUE,
  totalChartType: TOTAL_CHART_TYPES.BAR,
};

const ALLOWED_PREFERENCE_VALUES = {
  view: Object.values(CHART_VIEWS),
  totalMetric: Object.values(TOTAL_METRICS),
  totalChartType: Object.values(TOTAL_CHART_TYPES),
};

// O valor salvo pode ser de uma versão anterior da tela; o que não for mais uma opção válida volta ao padrão
export function sanitizePreferences(storedPreferences) {
  return Object.fromEntries(
    Object.entries(DEFAULT_PREFERENCES).map(([name, defaultValue]) => [
      name,
      ALLOWED_PREFERENCE_VALUES[name].includes(storedPreferences?.[name]) ? storedPreferences[name] : defaultValue,
    ])
  );
}

export function formatCompetenceLabel(competencia) {
  // Locale explícito: não depende do moment.locale global setado em Router.jsx
  const label = moment(competencia, 'MM/YYYY').locale('pt-BR').format('MMM/YY');
  return label.charAt(0).toUpperCase() + label.slice(1);
}

// O dodge do G2 (@antv/adjust) divide por (quantidade de competências - 1) quando `dodgePadding` está
// definido: com uma única competência o resultado é NaN e nenhuma barra é desenhada.
const DODGE_PADDING = 2;

export function resolveDodgePadding(competenceCount) {
  return competenceCount > 1 ? DODGE_PADDING : undefined;
}

// Cada CPF cai em uma única faixa por competência, então a soma das faixas não conta ninguém duas vezes
export function sumBandsByCompetence(competencias) {
  return competencias.map((competencia) => ({
    competenceLabel: formatCompetenceLabel(competencia.competencia),
    rawCompetencia: competencia.competencia,
    quantidadeCpfs: competencia.faixas.reduce((sum, faixa) => sum + faixa.quantidadeCpfs, 0),
    valorTotal: sumBandsTotalValue(competencia.faixas),
  }));
}

export function buildTooltipContent(items, { showDetailsLink = true } = {}) {
  const [item] = items;
  const container = document.createElement('div');
  if (!item) return container;

  // O G2 só aplica o estilo do tooltip (position absolute, fundo, sombra) quando o elemento retornado pelo
  // customContent tem esta classe; sem ela o conteúdo cai no fluxo da página, fora do cursor.
  container.className = 'g2-tooltip';

  const row = document.createElement('div');
  row.style.display = 'flex';
  row.style.alignItems = 'center';
  row.style.gap = '8px';

  const dot = document.createElement('span');
  dot.style.width = '8px';
  dot.style.height = '8px';
  dot.style.borderRadius = '50%';
  dot.style.backgroundColor = item.color;
  dot.style.display = 'inline-block';
  dot.style.flexShrink = '0';

  const value = document.createElement('span');
  value.style.fontWeight = '600';
  value.textContent = pluralize(item.data.quantidadeCpfs, 'cooperado', 'cooperados');

  row.appendChild(dot);
  row.appendChild(value);

  // RN8: além da quantidade de CPFs, o tooltip precisa trazer o valor total da faixa na competência
  const totalValue = document.createElement('div');
  totalValue.style.marginTop = '4px';
  totalValue.textContent = moneyFormat(item.data.valorTotal);

  container.appendChild(row);
  container.appendChild(totalValue);

  if (showDetailsLink) {
    const link = document.createElement('div');
    link.style.color = '#13c2c2';
    link.style.marginTop = '8px';
    link.textContent = 'Ver Detalhes';
    container.appendChild(link);
  }

  return container;
}

export function handleElementClick(evt, onSelectBand) {
  const record = evt.data?.data;
  if (record?.faixaId) {
    onSelectBand({ competencia: record.rawCompetencia, faixaId: record.faixaId, bandLabel: record.bandLabel });
  }
}

function ValueBandsChart({ folhaIds, competenciaInicial, competenciaFinal, onSelectBand }) {
  const [preferences, setPreferences] = usePersistedState(
    PREFERENCES_STORAGE_KEY,
    DEFAULT_PREFERENCES,
    sanitizePreferences
  );
  const { view, totalMetric, totalChartType } = preferences;
  const updatePreference = (name) => (e) => setPreferences({ ...preferences, [name]: e.target.value });
  const { data, isPending, isError, error, refetch } = usePayrollValueBandsChart({
    folhaIds,
    competenciaInicial,
    competenciaFinal,
  });

  const bandLabelById = useMemo(
    () => Object.fromEntries((data?.faixas ?? []).map((faixa) => [faixa.id, faixa.label])),
    [data]
  );

  const chartData = useMemo(
    () =>
      (data?.competencias ?? []).flatMap((competencia) =>
        competencia.faixas.map((faixa) => ({
          competenceLabel: formatCompetenceLabel(competencia.competencia),
          rawCompetencia: competencia.competencia,
          faixaId: faixa.faixaId,
          bandLabel: bandLabelById[faixa.faixaId],
          quantidadeCpfs: faixa.quantidadeCpfs,
          valorTotal: faixa.valorTotal,
        }))
      ),
    [data, bandLabelById]
  );

  const totalChartData = useMemo(() => sumBandsByCompetence(data?.competencias ?? []), [data]);

  const legendValues = useMemo(() => (data?.faixas ?? []).map((faixa) => faixa.label), [data]);
  const competenceValues = useMemo(
    () => (data?.competencias ?? []).map((competencia) => formatCompetenceLabel(competencia.competencia)),
    [data]
  );

  const tooltipDomStyles = {
    // O tema do G2 só dá padding horizontal ao container; o espaçamento vertical viria das margens
    // do título/lista padrão, que o customContent não usa.
    'g2-tooltip': { padding: '12px 16px', borderRadius: '8px' },
  };

  const byBandConfig = {
    data: chartData,
    xField: 'competenceLabel',
    yField: 'quantidadeCpfs',
    seriesField: 'bandLabel',
    isGroup: true,
    color: Object.values(COLOR_BY_BAND_ID),
    columnStyle: { radius: [4, 4, 0, 0] },
    dodgePadding: resolveDodgePadding(competenceValues.length),
    marginRatio: 0.15,
    legend: { position: 'right' },
    meta: {
      bandLabel: { values: legendValues },
      competenceLabel: { values: competenceValues },
    },
    tooltip: {
      shared: false,
      customContent: (_title, items) => buildTooltipContent(items),
      domStyles: tooltipDomStyles,
    },
    onReady: (plot) => {
      plot.on('element:click', (evt) => handleElementClick(evt, onSelectBand));
    },
  };

  const totalTooltip = {
    customContent: (_title, items) => buildTooltipContent(items, { showDetailsLink: false }),
    domStyles: tooltipDomStyles,
  };

  const isTotalView = view === CHART_VIEWS.TOTAL;

  return (
    <ChartCard>
      <ChartHeader>
        <ChartTitle>{isTotalView ? TOTAL_TITLE_BY_METRIC[totalMetric] : BY_BAND_TITLE}</ChartTitle>
        <Radio.Group
          optionType="button"
          value={view}
          onChange={updatePreference('view')}
          options={[
            { label: 'Por faixa', value: CHART_VIEWS.BY_BAND },
            { label: 'Total', value: CHART_VIEWS.TOTAL },
          ]}
        />
      </ChartHeader>
      {isTotalView && (
        <TotalChartOptions>
          <div>
            <OptionLabel>Exibir</OptionLabel>
            <Radio.Group
              size="small"
              optionType="button"
              value={totalMetric}
              onChange={updatePreference('totalMetric')}
              options={[
                { label: 'Valor da folha', value: TOTAL_METRICS.VALUE },
                { label: 'Cooperados', value: TOTAL_METRICS.COOPERATORS },
              ]}
            />
          </div>
          <div>
            <OptionLabel>Gráfico</OptionLabel>
            <Radio.Group
              size="small"
              optionType="button"
              value={totalChartType}
              onChange={updatePreference('totalChartType')}
              options={[
                { label: 'Barras', value: TOTAL_CHART_TYPES.BAR },
                { label: 'Colunas', value: TOTAL_CHART_TYPES.COLUMN },
                { label: 'Linha', value: TOTAL_CHART_TYPES.LINE },
              ]}
            />
          </div>
        </TotalChartOptions>
      )}
      {isError ? (
        <LoadError
          error={error}
          onRetry={refetch}
        />
      ) : (
        <Skeleton
          active
          loading={isPending}
        >
          {isTotalView ? (
            <TotalChart
              data={totalChartData}
              competenceValues={competenceValues}
              metric={totalMetric}
              chartType={totalChartType}
              tooltip={totalTooltip}
            />
          ) : (
            <ChartWrapper>
              <Column
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...byBandConfig}
              />
            </ChartWrapper>
          )}
        </Skeleton>
      )}
    </ChartCard>
  );
}

ValueBandsChart.propTypes = {
  folhaIds: PropTypes.arrayOf(PropTypes.number),
  competenciaInicial: PropTypes.string.isRequired,
  competenciaFinal: PropTypes.string.isRequired,
  onSelectBand: PropTypes.func.isRequired,
};

ValueBandsChart.defaultProps = {
  folhaIds: undefined,
};

export default ValueBandsChart;
